import { useEffect, useMemo, useState } from "react";
import PageBreadcrumbs from "../../layouts/PageBreadcrumbs.js";
import { SkeletonTable } from "../../components/Skeleton.js";
import { useTableState } from "../../lib/useTableState.js";
import TableToolbar from "../../components/TableToolbar.js";
import { Link } from "react-router-dom";
import { api, ApiError, TenantWorkspace, WorkspaceMember } from "../../lib/api.js";
import EmptyState from "../../components/EmptyState.js";

type RoleAssignment = Awaited<ReturnType<typeof api.userRoleAssignments>>["assignments"][number];

type CreateForm = {
  workspaceId: string;
  name: string;
  email: string;
  username: string;
  role: "admin" | "analyst" | "billing" | "support";
  password: string;
};

const EMPTY_CREATE_FORM: CreateForm = {
  workspaceId: "",
  name: "",
  email: "",
  username: "",
  role: "analyst",
  password: ""
};

export default function AdminMembers() {
  const [workspaces, setWorkspaces] = useState<TenantWorkspace[]>([]);
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [platformAssignments, setPlatformAssignments] = useState<RoleAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [workspaceFilter, setWorkspaceFilter] = useState("");
  const [createForm, setCreateForm] = useState<CreateForm | null>(null);
  const [creating, setCreating] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    setErr(null);
    try {
      const [d, ra] = await Promise.all([
        api.workspace(),
        api.userRoleAssignments({ status: "active" }).catch(() => ({ assignments: [] }))
      ]);
      setWorkspaces(d.workspaces ?? []);
      setMembers(d.members ?? []);
      setPlatformAssignments((ra.assignments ?? []).filter((a) => a.scope === "platform"));
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  async function submitCreate() {
    if (!createForm) return;
    if (!createForm.workspaceId) {
      setErr("Pick a target workspace.");
      return;
    }
    setCreating(true);
    setErr(null);
    try {
      await api.addTenantMember(createForm.workspaceId, {
        name: createForm.name.trim(),
        email: createForm.email.trim(),
        username: createForm.username.trim().toLowerCase(),
        role: createForm.role,
        password: createForm.password
      });
      setBanner(`Added ${createForm.username} to ${createForm.workspaceId}.`);
      setCreateForm(null);
      await refresh();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setCreating(false);
    }
  }

  useEffect(() => { void refresh(); }, []);

  const byId = useMemo(() => new Map(workspaces.map((w) => [w.id, w])), [workspaces]);
  // Workspace-scope filter still uses the controlled select; free-text
  // search is delegated to the TableToolbar (search across name/email/id).
  const filtered = useMemo(
    () => members.filter((m) => !workspaceFilter || m.workspaceId === workspaceFilter),
    [members, workspaceFilter]
  );
  const t = useTableState(filtered as unknown as Array<Record<string, unknown>>, {
    searchFields: ["name", "email", "id", "role", "status"],
    initialPageSize: 25
  });
  const tAdmins = useTableState(platformAssignments as unknown as Array<Record<string, unknown>>, {
    searchFields: ["userId", "roleKey", "grantedAt", "expiresAt"],
    initialPageSize: 25
  });

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>All members</h1>
          <p>Every member across every tenant workspace.</p>
        </div>
        <div className="actions">
          <button type="button" className="btn btn-primary" onClick={() => setCreateForm({ ...EMPTY_CREATE_FORM })}>
            + Add member
          </button>
        </div>
      </header>

      {banner && <div className="ts-alert ts-alert-info">{banner}</div>}

      <div className="toolbar" style={{ display: "flex", gap: 12, marginBottom: 16 }}>
        <input
          type="search"
          placeholder="Search by name, email, or id…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{ flex: 1, maxWidth: 320 }}
        />
        <select value={workspaceFilter} onChange={(e) => setWorkspaceFilter(e.target.value)}>
          <option value="">All workspaces</option>
          {workspaces.map((w) => (
            <option key={w.id} value={w.id}>
              {(w as { workspaceName?: string }).workspaceName ?? w.name ?? w.id}
            </option>
          ))}
        </select>
      </div>

      {err && <div className="alert alert-error">{err}</div>}

      {platformAssignments.length > 0 && (
        <section className="section">
          <div className="section-head"><h2>Platform administrators ({platformAssignments.length})</h2></div>
          <TableToolbar
            hideExport
            search={tAdmins.search}
            setSearch={tAdmins.setSearch}
            page={tAdmins.page}
            setPage={tAdmins.setPage}
            pageSize={tAdmins.pageSize}
            setPageSize={tAdmins.setPageSize}
            totalPages={tAdmins.totalPages}
            totalRows={tAdmins.totalRows}
            searchPlaceholder="Search admin / role…"
          />
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr><th>User</th><th>Role</th><th>Granted</th><th>Expires</th></tr>
              </thead>
              <tbody>
                {(tAdmins.paged as unknown as RoleAssignment[]).map((a) => {
                  const member = members.find((m) => m.id === a.userId);
                  return (
                    <tr key={a.id}>
                      <td>
                        <strong>{member?.name ?? a.userId}</strong>
                        {member?.email && <div className="soft" style={{ fontSize: 12 }}>{member.email}</div>}
                      </td>
                      <td><span className="pill pill-warning">{a.roleKey}</span></td>
                      <td className="soft">{new Date(a.assignedAt).toLocaleDateString()}</td>
                      <td className="soft">{a.expiresAt ? new Date(a.expiresAt).toLocaleDateString() : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {loading ? <SkeletonTable rows={5} columns={4} /> :
       filtered.length === 0 ? (
         <EmptyState
           icon="👥"
           title="No members match"
           description="Adjust the search or status filter, or invite someone new from a workspace's settings page."
         />
       ) : (
        <div>
          <TableToolbar
            hideExport
            search={t.search}
            setSearch={t.setSearch}
            page={t.page}
            setPage={t.setPage}
            pageSize={t.pageSize}
            setPageSize={t.setPageSize}
            totalPages={t.totalPages}
            totalRows={t.totalRows}
            searchPlaceholder="Search name / email / id…"
          />
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr><th>Member</th><th>Email</th><th>Role</th><th>Workspace</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {(t.paged as unknown as WorkspaceMember[]).map((m) => {
                const ws = m.workspaceId ? byId.get(m.workspaceId) : undefined;
                return (
                  <tr key={m.id}>
                    <td><strong>{m.name ?? "—"}</strong><div className="soft" style={{ fontSize: 12 }}><code>{m.id}</code></div></td>
                    <td className="soft">{m.email ?? "—"}</td>
                    <td>
                      <select
                        value={m.role ?? ""}
                        onChange={async (e) => {
                          const next = e.target.value;
                          if (!m.workspaceId || !m.id || next === m.role) return;
                          try {
                            const res = await api.setWorkspaceMemberRole(m.workspaceId, m.id, next);
                            setMembers((prev) =>
                              prev.map((row) => (row.id === m.id ? { ...row, role: res.member.role } : row))
                            );
                          } catch (e2) {
                            setErr(e2 instanceof ApiError ? e2.message : String(e2));
                          }
                        }}
                      >
                        {["owner", "admin", "analyst", "billing", "support"].map((r) => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      {ws ? (
                        <Link to={`/admin/workspaces/${encodeURIComponent(ws.id)}`}>
                          {(ws as { workspaceName?: string }).workspaceName ?? ws.name ?? ws.id}
                        </Link>
                      ) : m.workspaceId ?? "—"}
                    </td>
                    <td>{m.status ?? "—"}</td>
                    <td>
                      {m.workspaceId && String(m.role ?? "") !== "owner" && (
                        <button
                          className="btn btn-sm"
                          type="button"
                          onClick={async () => {
                            if (!window.confirm(`Remove ${m.name ?? m.email ?? m.id} from ${ws ? ((ws as { workspaceName?: string }).workspaceName ?? ws.name ?? ws.id) : m.workspaceId}?`)) return;
                            try {
                              await api.removeTenantMember(m.workspaceId!, m.id);
                              setMembers((prev) => prev.filter((row) => row.id !== m.id));
                            } catch (e) {
                              setErr(e instanceof ApiError ? e.message : String(e));
                            }
                          }}
                          style={{ color: "#b91c1c" }}
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              </tbody>
            </table>
          </div>
        </div>
       )}

      {createForm && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50
          }}
          onClick={(e) => { if (e.target === e.currentTarget && !creating) setCreateForm(null); }}
        >
          <form
            className="card"
            style={{ width: "min(520px, 92vw)", maxHeight: "92vh", overflow: "auto", padding: 20 }}
            onSubmit={(e) => { e.preventDefault(); void submitCreate(); }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <h2 style={{ margin: 0, fontSize: 16 }}>Add workspace member</h2>
              <button type="button" className="btn btn-sm" onClick={() => !creating && setCreateForm(null)}>Close</button>
            </div>
            <div className="form-grid" style={{ display: "grid", gap: 12 }}>
              <label>
                Workspace <span style={{ color: "#b91c1c" }}>*</span>
                <select
                  value={createForm.workspaceId}
                  onChange={(e) => setCreateForm({ ...createForm, workspaceId: e.target.value })}
                  required
                >
                  <option value="">Pick a workspace…</option>
                  {workspaces.map((w) => {
                    const display = (w as { workspaceName?: string }).workspaceName ?? w.name ?? w.id;
                    return <option key={w.id} value={w.id}>{display} ({w.slug ?? w.id})</option>;
                  })}
                </select>
              </label>
              <label>
                Name
                <input value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} required />
              </label>
              <label>
                Email
                <input type="email" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} required />
              </label>
              <label>
                Username
                <input value={createForm.username} onChange={(e) => setCreateForm({ ...createForm, username: e.target.value })} required />
                <small className="muted" style={{ fontSize: 11 }}>Lowercased on submit. Used for login.</small>
              </label>
              <label>
                Role
                <select
                  value={createForm.role}
                  onChange={(e) => setCreateForm({ ...createForm, role: e.target.value as CreateForm["role"] })}
                >
                  <option value="admin">Admin</option>
                  <option value="analyst">Analyst</option>
                  <option value="billing">Billing</option>
                  <option value="support">Support</option>
                </select>
                <small className="muted" style={{ fontSize: 11 }}>
                  "Owner" is reserved for the workspace creator and can't be granted via this form.
                </small>
              </label>
              <label>
                Initial password
                <input
                  type="password"
                  value={createForm.password}
                  onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                  minLength={8}
                  required
                />
                <small className="muted" style={{ fontSize: 11 }}>
                  ≥8 chars. The new user should rotate it on first login.
                </small>
              </label>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
              <button type="button" className="btn" disabled={creating} onClick={() => setCreateForm(null)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={creating}>
                {creating ? "Creating…" : "Create member"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
